import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Cell, Divider, Modal, Navigation, Note, Spacer } from '@vinted/web-ui';
import useTracking from 'hooks/useTracking';
import useTranslate from 'hooks/useTranslate';
import InfoBanner from 'components/InfoBanner';
import ScrollableArea from 'components/ScrollableArea';
import { ExtraServicePolicyNote, ExtraServiceActivePayInMethod } from 'components/ExtraService';
import { InfoBannerScreen } from 'constants/info-banner';
import { PayInMethodCode } from 'constants/pay-in-method';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { Screen } from 'constants/tracking/screens';
import { ExtraServiceOrderStatus } from 'constants/extra-service';
import { clickEvent, viewScreenEvent } from 'libs/common/event-tracker/events';
import { getIsOrderFree, getClosetPromotionOrder, isOrderConfirmationDisabled } from 'state/closet-promotion/selectors';
import { getPaymentStatus, getWalletBalance, getActivePayInMethod, getSelectedCreditCard, getIsWalletBalanceInsufficient } from 'state/extra-service/selectors';
import OrderSummary from './OrderSummary';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const ClosetPromotionOrderConfirmationModal = _ref => {
  let {
    show,
    onBack,
    onConfirmOrder,
    onShowSalesTaxModal,
    onPaymentMethodSelect
  } = _ref;
  const translate = useTranslate('closet_promotion.order_confirmation_modal');
  const translatePayInMethodError = useTranslate('payment_method_selection.errors');
  const {
    track
  } = useTracking();
  const {
    0: payInMethodError,
    1: setPayInMethodError
  } = useState();
  const isOrderFree = useSelector(getIsOrderFree);
  const walletBalance = useSelector(getWalletBalance);
  const selectedCreditCard = useSelector(getSelectedCreditCard);
  const orderStatus = useSelector(getPaymentStatus);
  const closetPromotionOrder = useSelector(getClosetPromotionOrder);
  const isConfirmationDisabled = useSelector(isOrderConfirmationDisabled);
  const activePayInMethod = useSelector(state => getActivePayInMethod(state, closetPromotionOrder === null || closetPromotionOrder === void 0 ? void 0 : closetPromotionOrder.payable.amount));
  const isWalletBalanceInsufficient = useSelector(state => getIsWalletBalanceInsufficient(state, closetPromotionOrder === null || closetPromotionOrder === void 0 ? void 0 : closetPromotionOrder.payable.amount));
  const isOrderValid = !!activePayInMethod || isOrderFree;
  useEffect(() => {
    if (!show) return;
    track(viewScreenEvent({
      screen: Screen.ClosetPromotionOrderReview
    }));
  }, [show, track]);
  useEffect(() => {
    if (isOrderValid && payInMethodError) {
      setPayInMethodError(undefined);
    }
  }, [isOrderValid, payInMethodError]);

  function handleShowSalesTaxModal() {
    onShowSalesTaxModal();
    track(clickEvent({
      target: ClickableElement.SalesTaxInfo,
      screen: Screen.ClosetPromotionOrderConfirmation,
      targetDetails: JSON.stringify({
        vas_order_id: closetPromotionOrder === null || closetPromotionOrder === void 0 ? void 0 : closetPromotionOrder.id
      })
    }));
  }

  function renderNavigation() {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Cell, {
        children: /*#__PURE__*/_jsx(Navigation, {
          body: translate('title'),
          testId: "closet-promotion-confirmation-modal-title"
        })
      }), /*#__PURE__*/_jsx("div", {
        className: "u-padding-horizontal-medium",
        children: /*#__PURE__*/_jsx(InfoBanner, {
          screen: InfoBannerScreen.VASCheckout
        })
      }), /*#__PURE__*/_jsx(Divider, {})]
    });
  }

  function renderOrderSummary() {
    if (!closetPromotionOrder) return null;
    return /*#__PURE__*/_jsx(OrderSummary, {
      order: closetPromotionOrder,
      onShowSalesTaxModal: handleShowSalesTaxModal
    });
  }

  function renderWalletAvailabilityNote() {
    const isWalletSelected = (activePayInMethod === null || activePayInMethod === void 0 ? void 0 : activePayInMethod.code) === PayInMethodCode.Wallet;
    if (isWalletBalanceInsufficient || isWalletSelected) return null;
    return /*#__PURE__*/_jsx(Note, {
      alignment: Note.Alignment.Center,
      text: translate('wallet_funds_availability_note')
    });
  }

  function handleConfirmOrder() {
    if (!isOrderValid) {
      setPayInMethodError(translatePayInMethodError('not_selected_payment_method'));
      return;
    }

    if (!closetPromotionOrder) return;
    track(clickEvent({
      screen: Screen.ClosetPromotionOrderConfirmation,
      target: ClickableElement.ConfirmClosetPromotionOrder,
      targetDetails: "".concat(closetPromotionOrder.id)
    }));
    onConfirmOrder();
  }

  function renderBody() {
    if (!closetPromotionOrder) return null;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [renderOrderSummary(), /*#__PURE__*/_jsx(Divider, {}), !isOrderFree && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ExtraServiceActivePayInMethod, {
          error: payInMethodError,
          walletBalance: walletBalance,
          selectedCreditCard: selectedCreditCard,
          activePayInMethod: activePayInMethod,
          onPaymentMethodSelect: onPaymentMethodSelect
        }), /*#__PURE__*/_jsx(Divider, {})]
      }), /*#__PURE__*/_jsx(ExtraServicePolicyNote, {}), /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Large
      }), renderWalletAvailabilityNote()]
    });
  }

  function renderActions() {
    return /*#__PURE__*/_jsxs(Cell, {
      children: [/*#__PURE__*/_jsx(Button, {
        disabled: isConfirmationDisabled,
        isLoading: orderStatus === ExtraServiceOrderStatus.Processing,
        text: translate('actions.place_order'),
        styling: Button.Styling.Filled,
        onClick: handleConfirmOrder,
        testId: "closet-promotion-order-confirmation--confirm-button"
      }), /*#__PURE__*/_jsx(Spacer, {}), /*#__PURE__*/_jsx(Button, {
        styling: Button.Styling.Flat,
        text: translate('actions.back'),
        testId: "closet-promotion-checkout-back-button",
        onClick: onBack
      })]
    });
  }

  return /*#__PURE__*/_jsx(Modal, {
    show: show,
    hasScrollableContent: true,
    children: /*#__PURE__*/_jsxs("div", {
      className: "u-fill-width",
      children: [renderNavigation(), /*#__PURE__*/_jsxs(ScrollableArea, {
        children: [renderBody(), renderActions()]
      })]
    })
  });
};

export default ClosetPromotionOrderConfirmationModal;